import pickBy from 'lodash/pickBy';
import { isObject } from './type';
import { arrify } from './arrayUtils';
import { identity } from './fp';

/**
 * Filter the object entries to get an reduced object with the entries that
 * satisfy a given predicate.
 *
 * @param {Object} obj - The target object.
 * @param {function(string, *):boolean} predicate - Function receiving
 *   (key, value).
 * @returns {Object} The reduced object.
 *
 * @example
 * var obj = { a: 1, b: 2, c: 3 };
 * var res = filter(obj, function (k, v) { return v > 1; });
 * // res => { b: 2, c: 3 };
 */
function filter(obj, predicate) {
  const result = {};
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = obj[key];
    if (predicate(key, value)) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Finds the first entry [key, value] in the object that satisfies
 * the predicate.
 *
 * @param {Object} obj - The target object.
 * @param {function(string, *):boolean} predicate - Function receiving
 *   (key, value).
 * @returns {[string, *]|undefined} The first matching entry as
 *   [key, value], or undefined if none match.
 *
 * @example
 * var obj = { a: 1, b: 2, c: 3 };
 * var res = findEntry(obj, function (k, v) { return v > 1; });
 * // res => ['b', 2]
 */
function findEntry(obj, predicate) {
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = obj[key];
    if (predicate(key, value)) {
      return [key, value];
    }
  }
  return undefined;
}

/**
 * Build an object based on the non-object seed value.
 * @param {*} val seed value
 * @param {Object} parsers
 * @param {function(*):string} parsers.parseKey Build a key from the seed or
 * a part of it
 * @param {function(*):*} parsers.parseValue Build a value from the seed or
 * a part of it
 * @returns {Object} new object
 */
function objectify(val, { parseKey = identity, parseValue = identity } = {}) {
  let objectified;
  if (isObject(val)) {
    objectified = val;
  } else {
    objectified = arrify(val).reduce((obj, currentSeed) => {
      // eslint-disable-next-line no-param-reassign
      obj[parseKey(currentSeed)] = parseValue(currentSeed);
      return obj;
    }, {});
  }
  return objectified;
}

/**
 * Creates an object composed of the object properties that begin with keyPrefix
 * @param {Object} object - The source object
 * @param {string} keyPrefix - The prefix of the targeted keys
 * @return {Object} The new object
 */
const pickByKeyPrefix = (object, keyPrefix) =>
  pickBy(object, (_, key) => key.indexOf(keyPrefix) === 0);

export { objectify, filter, findEntry, pickBy, pickByKeyPrefix };
